<template>
  <article>
    <AppSpinner v-if="cartStore.isLoading" />

    <template v-else>
      <template v-if="cartStore.cart?.items?.length > 0">
        <article class="cart">
          <h1>Added to your cart</h1>

          <!-- Cart items -->
          <ul class="cart-items">
            <template
              v-for="item in cartStore.cart?.items"
              :key="item.id"
            >
              <CartItem
                v-model:deleting="deleting"
                :item="item"
                :is-photo-pack="item.type === PhotoType.Pack"
                @customise="handleShowCustomizeModal(item.id)"
                @remove="handleDeleteItem(item.id)"
              />
            </template>
          </ul>

          <CartDeliveryFee
            v-if="deliveryFee"
            :fee="deliveryFee"
          />

          <CartTotal :total="cartTotal" />

          <AppContainer class="w-full flex justify-center xs:justify-end mt-6 sm:mt-8 p-0">
            <AppButton
              class="rounded-none btn-solid btn-secondary bg-secondary-light hover:bg-secondary py-3 px-6 text-[20px] mr-6 md:mr-0"
              @click="!authStore.user ? showAuthenticationModal = true : proceedToCheckout()"
            >
              Proceed to checkout
              <FontAwesomeIcon :icon="faCircleArrowRight" />
            </AppButton>
          </AppContainer>
        </article>
      </template>

      <!--no items in cart-->
      <template v-else>
        <EmptyViewPlaceholder message="There currently are no items in your cart" />
      </template>
    </template>

    <template v-if="cartStore.isLoading">
      <AppSpinner class="page-spinner" />
    </template>

    <!-- Customize photo form -->
    <Transition name="fade">
      <AppModal
        v-if="showCustomizePhotoModal"
        :disabled="isSubmitting"
        class="customize-photo-modal"
        @close="showCustomizePhotoModal = false"
      >
        <CustomizePhotoForm
          v-model:is-submitting="isSubmitting"
          :order-item="selectedOrderItem"
          :photo="selectedOrderItem.photo"
          @close="handleCloseCustomizePhotoModal"
        />
      </AppModal>
    </Transition>

    <!-- Authentication modal -->
    <Transition name="fade">
      <AuthenticationModal
        v-if="showAuthenticationModal"
        :intent="IntentType.Checkout"
        @submit="proceedToCheckout"
        @close="showAuthenticationModal = false"
      />
    </Transition>
  </article>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import { AppButton, AppContainer, AppModal, AppSpinner } from '@/components/app';
import { CustomizePhotoForm } from '@/components/forms';
import { AuthenticationModal } from '@/components/modals';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCircleArrowRight } from '@fortawesome/pro-regular-svg-icons';

import { useAuthStore, useCartStore } from '@/store';
import { IntentType, PhotoType } from '@/const.js';
import { CartDeliveryFee, CartItem, CartTotal } from '@/components/cart';
import { EmptyViewPlaceholder } from '@/components/placeholders';

const cartStore = useCartStore();
const authStore = useAuthStore();
const router = useRouter();

const isSubmitting = ref(false);
const selectedOrderItem = ref(null);
const showCustomizePhotoModal = ref(false);
const deleting = ref([]);
const showAuthenticationModal = ref(false);

const handleDeleteItem = async (orderId) => {
  deleting.value.push(orderId);

  await cartStore.removeItem(orderId);
  await cartStore.loadCart();

  const index = deleting.value.indexOf(orderId);
  if (index !== -1) {
    deleting.value.splice(index, 1);
  }
};

const handleShowCustomizeModal = (id) => {
  if (!deleting.value.includes(id)) {
    showCustomizePhotoModal.value = true;

    selectedOrderItem.value = cartStore.cart.items.find(item => item.id === id);
  }
};

const handleCloseCustomizePhotoModal = () => {
  showCustomizePhotoModal.value = false;

  selectedOrderItem.value = null;
};

const proceedToCheckout = async () => {
  if (deleting.value.length === 0) {
    await router.push({ name: 'app_cart_confirmation' });
  }
};

const cartTotal = computed(() => {
  return cartStore.cart.totalCost?.toFixed(2) || 0;
});
const deliveryFee = computed(() => {
  return cartStore.cart.deliveryFee ? cartStore.cart.deliveryFee?.toFixed(2) : null;
});

</script>
